import React from "react";
import ReactDOM from "react-dom";
import cn from "./helpers/cn";

export type MenuOriginVertical = "top" | "center" | "bottom" | number;
export type MenuOriginHorizontal = "left" | "center" | "right" | number;

export interface MenuOrigin {
  vertical: MenuOriginVertical;
  horizontal: MenuOriginHorizontal;
}

// Mapa elevation -> shadow (mesma escala do Paper).
const elevationMap: Record<number, string> = {
  0: "shadow-none",
  1: "shadow-sm",
  2: "shadow",
  3: "shadow-md",
  4: "shadow-lg",
  5: "shadow-xl",
  6: "shadow-2xl",
  ...Object.fromEntries(Array.from({ length: 18 }, (_, i) => [i + 7, "shadow-2xl"])),
};

interface MenuProps {
  open: boolean;
  onClose?: (event: React.SyntheticEvent | Event, reason?: "backdropClick" | "escapeKeyDown" | "tabKeyDown") => void;
  anchorEl?: Element | null;
  anchorOrigin?: MenuOrigin;
  transformOrigin?: MenuOrigin;
  getContentAnchorEl?: null | ((el: Element) => Element);
  keepMounted?: boolean;
  elevation?: number;
  variant?: "menu" | "selectedMenu";
  autoFocus?: boolean;
  MenuListProps?: React.HTMLAttributes<HTMLUListElement>;
  PaperProps?: React.HTMLAttributes<HTMLDivElement>;
  BackdropProps?: React.HTMLAttributes<HTMLDivElement>;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

function resolveVertical(v: MenuOriginVertical, height: number): number {
  if (typeof v === "number") return v;
  if (v === "top") return 0;
  if (v === "center") return height / 2;

  return height; // bottom
}

function resolveHorizontal(h: MenuOriginHorizontal, width: number): number {
  if (typeof h === "number") return h;
  if (h === "left") return 0;
  if (h === "center") return width / 2;

  return width; // right
}

const Menu = React.forwardRef<HTMLDivElement, MenuProps>((props, ref) => {
  const {
    open,
    onClose,
    anchorEl,
    anchorOrigin = { vertical: "top", horizontal: "left" },
    transformOrigin = { vertical: "top", horizontal: "left" },
    keepMounted = false,
    elevation = 8,
    autoFocus = true,
    MenuListProps,
    PaperProps,
    BackdropProps,
    children,
    className,
    id,
  } = props;

  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number; }>({ top: 0, left: 0 });

  // Calcula posição do paper a partir de anchorEl + origins.
  const updatePosition = React.useCallback(() => {
    if (!open || !anchorEl || !paperRef.current) return;
    const anchorRect = anchorEl.getBoundingClientRect();
    const paperRect = paperRef.current.getBoundingClientRect();

    // Ponto de ancoragem no anchorEl (em coordenadas da viewport).
    const anchorTop = anchorRect.top + resolveVertical(anchorOrigin.vertical, anchorRect.height);
    const anchorLeft = anchorRect.left + resolveHorizontal(anchorOrigin.horizontal, anchorRect.width);

    // Ponto correspondente do paper (offset interno) que deve coincidir com anchor.
    const offsetTop = resolveVertical(transformOrigin.vertical, paperRect.height);
    const offsetLeft = resolveHorizontal(transformOrigin.horizontal, paperRect.width);

    let top = anchorTop - offsetTop;
    let left = anchorLeft - offsetLeft;

    // Clamp para manter dentro da viewport (igual ao MUI: 16px de margem).
    const margin = 16;
    const maxLeft = window.innerWidth - paperRect.width - margin;
    const maxTop = window.innerHeight - paperRect.height - margin;

    if (left < margin) left = margin;
    if (top < margin) top = margin;
    if (left > maxLeft) left = Math.max(margin, maxLeft);
    if (top > maxTop) top = Math.max(margin, maxTop);

    setPos({ top, left });
  }, [open, anchorEl, anchorOrigin.vertical, anchorOrigin.horizontal, transformOrigin.vertical, transformOrigin.horizontal]);

  React.useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition, children]);

  // Reposiciona em resize/scroll para acompanhar mudanças no layout.
  React.useEffect(() => {
    if (!open) return;
    const handler = () => updatePosition();

    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);

    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, updatePosition]);

  // Captura Escape e Tab para fechar (igual ao MUI Popover/Menu).
  React.useEffect(() => {
    if (!open) return;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose?.(event, "escapeKeyDown");
      } else if (event.key === "Tab") {
        onClose?.(event, "tabKeyDown");
      }
    };

    document.addEventListener("keydown", handleKey);

    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Foco automático no MenuList ao abrir.
  React.useEffect(() => {
    if (open && autoFocus && listRef.current) {
      listRef.current.focus();
    }
  }, [open, autoFocus]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    onClose?.(event, "backdropClick");
  };

  if (!open && !keepMounted) return null;
  if (typeof document === "undefined") return null;

  // Cria o portal direto no body para ficar fora de stacking contexts/clipping.
  return ReactDOM.createPortal(
    <div
      role="presentation"
      className={cn("fixed inset-0 z-[1300]", !open && "invisible pointer-events-none", className)}
    >
      {/* Backdrop transparente que captura clique fora */}
      <div
        {...BackdropProps}
        onClick={handleBackdropClick}
        aria-hidden="true"
        className={cn("fixed inset-0", BackdropProps?.className)}
      />

      {/* Paper posicionado absolutamente */}
      <div
        ref={(node) => {
          paperRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        id={id}
        {...PaperProps}
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          ...(PaperProps?.style || {}),
        }}
        className={cn(
          "bg-white rounded outline-none min-w-[112px] max-w-[calc(100%-32px)] max-h-[calc(100%-96px)] overflow-auto",
          elevationMap[elevation] ?? "shadow-lg",
          "animate-modal-in",
          PaperProps?.className
        )}
      >
        <ul
          ref={listRef}
          role="menu"
          tabIndex={-1}
          {...MenuListProps}
          className={cn("py-2 outline-none list-none m-0", MenuListProps?.className)}
        >
          {children}
        </ul>
      </div>
    </div>,
    document.body
  );
});

Menu.displayName = "Menu";

export default Menu;
